import { Link } from "react-router-dom";
import { Mail, Shield, CreditCard, Clock, Zap } from "lucide-react";
import { platforms } from "@/data/products";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslatedPlatformName } from "@/i18n/productTranslations";
import { getPlatformTheme } from "@/lib/platformColors";
import MultiColorLogo from "./MultiColorLogo";
import FooterCmsMenu from "./FooterCmsMenu";

const paymentMethods = [
  { name: "Visa", icon: "/images/payments/visa.webp", color: "hsl(220, 80%, 50%)" },
  { name: "Mastercard", icon: "/images/payments/mastercard.webp", color: "hsl(15, 90%, 50%)" },
  { name: "PayPal", icon: "/images/payments/paypal.webp", color: "hsl(210, 80%, 55%)" },
  { name: "Bitcoin", icon: "/images/payments/bitcoin.webp", color: "hsl(40, 95%, 50%)" },
  { name: "Ethereum", icon: "/images/payments/ethereum.webp", color: "hsl(230, 60%, 55%)" },
  { name: "USDT", icon: "/images/payments/usdt.webp", color: "hsl(160, 80%, 45%)" },
];

const SiteFooter = () => {
  const { t, lang } = useLanguage();

  return (
    <footer className="relative mt-16 border-t" style={{ background: 'linear-gradient(180deg, hsl(230 25% 7%), hsl(230 25% 5%))', borderColor: 'hsl(174 90% 50% / 0.15)' }}>
      {/* Top glow line */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, hsl(174 90% 50% / 0.5), hsl(280 80% 55% / 0.5), transparent)' }} />

      <div className="max-w-[1100px] mx-auto px-4 py-12 cyber-grid">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand + Page Links */}
          <div className="space-y-3">
            <div className="flex items-center">
              <MultiColorLogo className="h-20" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.footerDesc}</p>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-5 h-5 text-neon-purple flex-shrink-0" />
              <span className="text-muted-foreground">support@mehubarim.co.il</span>
            </div>
            <ul className="space-y-1.5 pt-1">
              <li><Link to="/blog" className="text-sm text-muted-foreground hover:text-primary transition-colors">📝 בלוג ומדריכים</Link></li>
              <li><Link to="/reviews" className="text-sm text-muted-foreground hover:text-primary transition-colors">{t.customerReviewsLink}</Link></li>
              <li><Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">📋 תקנון האתר</Link></li>
              <li><Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">🔒 מדיניות פרטיות</Link></li>
              <li><Link to="/accessibility" className="text-sm text-muted-foreground hover:text-primary transition-colors">♿ הצהרת נגישות</Link></li>
              <li><Link to="/cookie-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">{t.cookiePolicy}</Link></li>
            </ul>
            <FooterCmsMenu />
          </div>

          {/* All Platforms in one column */}
          <div className="sm:col-span-1 lg:col-span-2 space-y-3">
            <h3 className="font-display font-bold text-base tracking-wider" style={{
              background: 'linear-gradient(90deg, hsl(174 90% 50%), hsl(280 80% 55%), hsl(320 85% 55%), hsl(45 95% 55%))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>{t.platforms}</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {platforms.map((platform) => {
                const theme = getPlatformTheme(platform.slug);
                const isLinkedin = platform.slug === 'linkedin';
                const iconSize = isLinkedin ? 'w-12 h-12' : 'w-9 h-9';
                return (
                  <Link key={platform.id} to={`/${platform.slug}`} className="text-sm transition-all duration-300 flex items-center gap-2 hover:translate-x-[-4px]" style={{ color: theme.primary }}>
                    <img src={platform.icon} alt={getTranslatedPlatformName(platform.slug, lang)} className={`${iconSize} object-contain flex-shrink-0`} style={{ filter: `drop-shadow(0 0 6px ${theme.glow})` }} />
                    <span className="truncate" style={{ textShadow: theme.textShadow }}>{getTranslatedPlatformName(platform.slug, lang)}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Info */}
          <div className="space-y-4">
            <h3 className="font-display font-bold text-base tracking-wider" style={{
              background: 'linear-gradient(90deg, hsl(174 90% 50%), hsl(280 80% 55%), hsl(320 85% 55%), hsl(45 95% 55%))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>{t.whyUs}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-neon-green mt-0.5 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 6px hsl(150 80% 50% / 0.5))' }} />
                <span className="text-sm text-muted-foreground">{t.safeService}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-neon-purple mt-0.5 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 6px hsl(280 80% 55% / 0.5))' }} />
                <span className="text-sm text-muted-foreground">{t.autoStart}</span>
              </li>
              <li className="flex items-start gap-3">
                <CreditCard className="w-6 h-6 text-neon-pink mt-0.5 flex-shrink-0" style={{ filter: 'drop-shadow(0 0 6px hsl(320 85% 55% / 0.5))' }} />
                <span className="text-sm text-muted-foreground">{t.securePayment}</span>
              </li>
            </ul>

            <div className="pt-2">
              <p className="text-xs text-muted-foreground font-semibold mb-2">{t.paymentMethods}</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {paymentMethods.map((pm) => (
                  <div
                    key={pm.name}
                    className="w-20 h-14 rounded-lg border bg-muted/30 flex items-center justify-center p-2 transition-all duration-300 hover:scale-105"
                    title={pm.name}
                    style={{
                      borderColor: pm.color.replace(')', ' / 0.4)').replace('hsl', 'hsla').replace('hsla', 'hsl'),
                      boxShadow: `0 0 10px ${pm.color.replace(')', ' / 0.15)').replace('hsl', 'hsla').replace('hsla', 'hsl')}`,
                    }}
                  >
                    <img src={pm.icon} alt={pm.name} className="w-14 h-10 object-contain" loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                        (e.target as HTMLImageElement).parentElement!.innerHTML = `<span class="text-[9px] font-bold text-muted-foreground">${pm.name}</span>`;
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t" style={{ borderColor: 'hsl(174 90% 50% / 0.1)' }}>
        <div className="max-w-[1100px] mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <p className="flex items-center gap-1">
            <Zap className="w-3 h-3 text-primary" />
            © {new Date().getFullYear()} {t.brandName}. {t.allRightsReserved}
          </p>
          <p className="neon-text-cyan text-xs">mehubarim.co.il</p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
